/*============================================================================*/

/**
 * Setter functions
 *
 * These functions are responsible for applying or modifying values,
 * attributes, classes, or states on DOM elements. Unlike `renders.js`,
 * which handles where and how elements appear, or `generate.js`, which
 * creates raw components, setter functions only update existing elements
 * or data in a controlled and reusable way.
 */

/*============================================================================*/


/**
 * Set AOS attributes on a DOM element
 *
 * This function applies a set of AOS (Animate On Scroll) attributes to the
 * given element. Only attributes with defined, non-null, and non-empty values
 * are applied. Keys that do not start with "data-" are automatically prefixed.
 *
 * @param {Object} args - Function arguments
 * @param {HTMLElement} args.element - Target DOM element
 * @param {Object} args.aosArgs - Key-value pairs of AOS attributes
 */
function setAosAttributes({ element, aosArgs } = {})
{
    if (!element || !aosArgs) return

    Object.entries(aosArgs).forEach(([key, value]) =>
    {
        if (value !== undefined && value !== null && value !== '')
        {
            element.setAttribute(key.startsWith('data-') ? key : `data-${key}`, value)
        }
    })
}

/**
 * Populate form fields with UTM parameters from the URL
 *
 * Extracts UTM values from the current URL, assembles a summary string,
 * and fills corresponding hidden inputs in the given form.
 *
 * @param {Object} args - Function arguments
 * @param {string} args.formSelector - jQuery selector of the target form
 */
function setUtmFormFields({ formSelector })
{
    const utmParams = {
        source: getUrlParameterValue({ param: 'utm_source' }),
        medium: getUrlParameterValue({ param: 'utm_medium' }),
        campaign: getUrlParameterValue({ param: 'utm_campaign' }),
        content: getUrlParameterValue({ param: 'utm_content' }),
        term: getUrlParameterValue({ param: 'utm_term' }),
        id: getUrlParameterValue({ param: 'utm_id' })
    };

    const utmParts = Object.entries(utmParams)
        .filter(([ , value ]) => value)
        .map(([ key, value ]) => `${key}=${value}`);

    const cUtmzValue = utmParts.join('|');

    const form = jQuery(formSelector);
    form.find('input[name="traffic_source"]').val(utmParams.source);
    form.find('input[name="c_utmz"]').val(cUtmzValue);
}

/**
 * Reset form fields
 *
 * Resets inputs, textareas, dropdowns, and checkables within a given form.
 * Supports an ignore list to skip specific elements by ID, class, or name.
 *
 * @param {Object} params - Reset configuration
 * @param {string} params.formId - ID of the target form
 * @param {Array<Object>} [params.ignore=[]] - Elements to skip during reset
 * @param {string} params.ignore[].type - Filter type ("id" | "class" | "name")
 * @param {string} params.ignore[].value - Value of the filter to match
 */
function resetFormFields({ formId, ignore = [] })
{
    const form = jQuery(`#${formId}`)

    const shouldIgnore = (element) =>
    {
        return ignore.some(({ type, value }) =>
        {
            const $el = jQuery(element)

            if (type === 'id') return $el.attr('id') === value
            if (type === 'class') return $el.hasClass(value)
            if (type === 'name') return $el.attr('name') === value

            return false
        })
    }

    form.find('input, textarea').each(function ()
    {
        if (shouldIgnore(this)) return
        jQuery(this).val('').trigger('change')
    })

    form.find('.compDropdown').each(function ()
    {
        if (shouldIgnore(this)) return

        const dropdownId = jQuery(this).attr('id')

        if (dropdownId)
        {
            Comp_DropdownDeselect(dropdownId)
        }
    })

    form.find('.compCheckable, .checkableOption').each(function ()
    {
        if (shouldIgnore(this)) return

        jQuery(this)
            .attr('status', 'false')
            .find('span.material-symbols-outlined, span.material-symbols-filled')
            .text('check_box_outline_blank')
    })
}

/**
 * Set the text content of elements with a formatted localized date.
 *
 * - Reads the raw date using `getDate`.
 * - Formats the date using `formatDate`.
 * - Sets the formatted string as the element's text.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.selector - Selector or elements to update.
 */
function setFormattedDate({ selector } = {})
{
    jQuery(selector).each(function()
    {
        const rawDate = getDate({ element: this });

        if (!isEmpty(rawDate))
        {
            const formattedDate = formatDate({
                dateInput: rawDate,
                options:
                {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                }
            });

            jQuery(this).text(formattedDate);
        }
    });
}